import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { filesAPI } from '../../services/api';
import { 
  FileText, Upload, Download, Trash2, Search, Filter, Folder, 
  File, Image, FileAudio, Loader2, CheckCircle, AlertCircle, X, RefreshCw
} from 'lucide-react';

const FilesPage = () => {
  const { clientUser } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [fileTypeFilter, setFileTypeFilter] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);

  const fileTypeIcons = {
    'application/pdf': { icon: FileText, color: 'text-red-400' },
    'image/jpeg': { icon: Image, color: 'text-blue-400' },
    'image/png': { icon: Image, color: 'text-blue-400' },
    'audio/mpeg': { icon: FileAudio, color: 'text-purple-400' },
    'audio/wav': { icon: FileAudio, color: 'text-purple-400' },
    'default': { icon: File, color: 'text-gray-400' }
  };

  const getFileIcon = (contentType) => {
    return fileTypeIcons[contentType] || fileTypeIcons.default;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const fetchFiles = async () => {
    if (!clientUser?.id) return;
    
    setLoading(true);
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (fileTypeFilter) params.file_type = fileTypeFilter;
      
      const response = await filesAPI.getFiles(clientUser.id, params);
      if (response.data.success) {
        setFiles(response.data.files);
      }
    } catch (err) {
      console.error('Error fetching files:', err);
      setError('Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => { await fetchFiles(); })();
  }, [clientUser?.id, searchQuery, fileTypeFilter]);

  const handleFileUpload = async (e) => {
    const uploadFiles = Array.from(e.target.files);
    if (uploadFiles.length === 0) return;
    
    setUploading(true);
    setError(null);
    
    let successCount = 0;
    let errorCount = 0;
    
    for (const file of uploadFiles) {
      try {
        await filesAPI.upload(clientUser.id, file, 'document');
        successCount++;
      } catch (err) {
        console.error('Error uploading file:', err);
        errorCount++;
      }
    }
    
    setUploading(false);
    
    if (successCount > 0) {
      setSuccess(`Successfully uploaded ${successCount} file(s)`);
      fetchFiles();
      setTimeout(() => setSuccess(null), 3000);
    }
    if (errorCount > 0) {
      setError(`Failed to upload ${errorCount} file(s)`);
    }
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDownload = async (file) => {
    try {
      const response = await filesAPI.downloadFile(clientUser.id, file.id);
      const blob = response.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.original_filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download file');
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;
    
    try {
      await filesAPI.deleteFile(clientUser.id, fileId);
      setSuccess('File deleted successfully');
      fetchFiles();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to delete file');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedFiles.length === 0) return;
    if (!window.confirm(`Delete ${selectedFiles.length} files?`)) return;
    
    try {
      await filesAPI.bulkDelete(clientUser.id, selectedFiles);
      setSuccess(`Deleted ${selectedFiles.length} files`);
      setSelectedFiles([]);
      fetchFiles();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to delete files');
    }
  };

  const toggleFileSelect = (fileId) => {
    setSelectedFiles(prev => 
      prev.includes(fileId) 
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedFiles.length === files.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(files.map(f => f.id));
    }
  };

  if (loading && files.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Alerts */}
      {success && (
        <div className="fixed top-4 right-4 z-50 bg-green-500/20 border border-green-500/30 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-400" />
          <span className="text-green-400">{success}</span>
        </div>
      )}
      {error && (
        <div className="fixed top-4 right-4 z-50 bg-red-500/20 border border-red-500/30 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <span className="text-red-400">{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Files</h1>
          <p className="text-gray-400">Manage documents and files for your AI agents</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={fetchFiles}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#1a1a1f] border border-gray-700 rounded-lg text-gray-400 hover:text-white"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileUpload}
            className="hidden"
          />
          <button 
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 px-4 py-2.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50"
          >
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
            {uploading ? 'Uploading...' : 'Upload Files'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <Folder className="w-8 h-8 text-teal-400" />
            <div>
              <p className="text-gray-400 text-sm">Total Files</p>
              <p className="text-2xl font-bold text-white">{files.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8 text-red-400" />
            <div>
              <p className="text-gray-400 text-sm">Documents</p>
              <p className="text-2xl font-bold text-white">
                {files.filter(f => f.file_type === 'document').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <FileAudio className="w-8 h-8 text-purple-400" />
            <div>
              <p className="text-gray-400 text-sm">Audio</p>
              <p className="text-2xl font-bold text-white">
                {files.filter(f => f.file_type === 'voice_sample').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <File className="w-8 h-8 text-blue-400" />
            <div>
              <p className="text-gray-400 text-sm">Total Size</p>
              <p className="text-2xl font-bold text-white">
                {formatFileSize(files.reduce((acc, f) => acc + (f.size || 0), 0))}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search files..."
            className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg pl-10 pr-4 py-2.5 text-white outline-none focus:border-teal-400"
          />
        </div>
        <select
          value={fileTypeFilter}
          onChange={(e) => setFileTypeFilter(e.target.value)}
          className="px-4 py-2.5 bg-[#1a1a1f] border border-gray-700 rounded-lg text-gray-300 outline-none focus:border-teal-400"
        >
          <option value="">All Types</option>
          <option value="document">Documents</option>
          <option value="knowledge_base">Knowledge Base</option>
          <option value="voice_sample">Voice Samples</option>
        </select>
        {selectedFiles.length > 0 && (
          <button
            onClick={handleBulkDelete}
            className="flex items-center gap-2 px-4 py-2.5 bg-red-500/20 border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/30"
          >
            <Trash2 className="w-5 h-5" />
            Delete ({selectedFiles.length})
          </button>
        )}
      </div>

      {/* Files Table */}
      <div className="bg-[#0d1117] border border-gray-800 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-[#1a1a1f] border-b border-gray-800">
              <th className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  checked={selectedFiles.length === files.length && files.length > 0}
                  onChange={toggleSelectAll}
                  className="rounded border-gray-600"
                />
              </th>
              <th className="text-left px-6 py-3 text-gray-400 text-sm font-medium">Name</th>
              <th className="text-left px-6 py-3 text-gray-400 text-sm font-medium">Type</th>
              <th className="text-left px-6 py-3 text-gray-400 text-sm font-medium">Size</th>
              <th className="text-left px-6 py-3 text-gray-400 text-sm font-medium">Uploaded</th>
              <th className="text-right px-6 py-3 text-gray-400 text-sm font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {files.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-16">
                  <Folder className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">No files found</p>
                  <p className="text-gray-500 text-sm">Upload files to get started</p>
                </td>
              </tr>
            ) : (
              files.map((file) => {
                const { icon: FileIcon, color } = getFileIcon(file.content_type);
                return (
                  <tr key={file.id} className="border-b border-gray-800 hover:bg-[#1a1a1f]/50">
                    <td className="w-12 px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedFiles.includes(file.id)}
                        onChange={() => toggleFileSelect(file.id)}
                        className="rounded border-gray-600"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center ${color}`}>
                          <FileIcon className="w-5 h-5" />
                        </div>
                        <span className="text-white font-medium truncate max-w-[300px]">
                          {file.original_filename}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-gray-800 text-gray-400 text-xs rounded capitalize">
                        {file.file_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-400">
                      {formatFileSize(file.size)}
                    </td>
                    <td className="px-6 py-4 text-gray-400">
                      {new Date(file.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button 
                          onClick={() => handleDownload(file)}
                          className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                          title="Download"
                        >
                          <Download className="w-4 h-4 text-gray-400" />
                        </button>
                        <button 
                          onClick={() => handleDelete(file.id)}
                          className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FilesPage;
